/**
 * 差し替え合成（元音源 + 連打フレーズ）のスケジューリングと
 * オフラインレンダリング → WAV 書き出し。
 *
 * 合成タイムライン:
 *   [0, startSec)                   元音源をそのまま再生
 *   [startSec, startSec+phraseLen)  連打フレーズ（各ヒットは常に startSec から再生し直す）
 *   [startSec+phraseLen, 末尾)      元音源の続きを同位置で再生（全体長は元と同じ）
 *
 * scheduleComposite は試聴（useStutterEngine）と WAV 書き出しで共有する。
 * window は合成タイムライン上の再生範囲。試聴時はフレーズ前後 1 小節に
 * 絞るが、フレーズ全体は常に window に含まれる前提（ヒットの部分再生は
 * 考えない）。
 */

import { encodeWav } from "../../phrase-bloom/utils/wavEncoder";
import {
  buildStutterPlan,
  phraseLengthSec,
  type RendaParams,
} from "./stutterPlan";

/** スライス境界・セグメント境界のデクリックフェード（秒） */
const FADE_SEC = 0.003;
/** これ未満の再生区間はスケジュールしない */
const MIN_SCHEDULE_SEC = 0.001;

export interface CompositeWindow {
  /** 合成タイムライン上の再生開始位置（秒） */
  windowStartSec: number;
  /** 合成タイムライン上の再生終了位置（秒） */
  windowEndSec: number;
  /** windowStartSec に対応する AudioContext 時刻 */
  timeOrigin: number;
}

/** 合成タイムラインの全長（元音源長 or フレーズ終端の長い方） */
export const compositeTotalSec = (
  buffer: AudioBuffer,
  params: RendaParams,
): number =>
  Math.max(
    buffer.duration,
    params.startSec + phraseLengthSec(params.lengthSixteenths, params.bpm),
  );

/**
 * 1 本の AudioBufferSourceNode をデクリックフェード付きでスケジュールする。
 * durSec は出力（実時間）での可聴長。playbackRate 併用時は停止時刻で
 * 制御するため、消費される元音源は durSec × rate 秒になる。
 */
const scheduleSlice = (
  ctx: BaseAudioContext,
  destination: AudioNode,
  buffer: AudioBuffer,
  when: number,
  srcOffsetSec: number,
  durSec: number,
  playbackRate: number,
  sources: AudioScheduledSourceNode[],
): void => {
  if (durSec < MIN_SCHEDULE_SEC) return;

  const gain = ctx.createGain();
  const fade = Math.min(FADE_SEC, durSec / 4);
  gain.gain.setValueAtTime(0, when);
  gain.gain.linearRampToValueAtTime(1, when + fade);
  gain.gain.setValueAtTime(1, when + durSec - fade);
  gain.gain.linearRampToValueAtTime(0, when + durSec);
  gain.connect(destination);

  const src = ctx.createBufferSource();
  src.buffer = buffer;
  src.playbackRate.value = playbackRate;
  src.connect(gain);
  src.start(when, srcOffsetSec);
  src.stop(when + durSec);
  sources.push(src);
};

/**
 * 差し替え合成の全イベントをスケジュールし、生成したソース一覧を返す
 * （試聴側が停止時に stop() を呼ぶため）。
 */
export const scheduleComposite = (
  ctx: BaseAudioContext,
  destination: AudioNode,
  buffer: AudioBuffer,
  params: RendaParams,
  window: CompositeWindow,
): AudioScheduledSourceNode[] => {
  const sources: AudioScheduledSourceNode[] = [];
  const { windowStartSec, windowEndSec, timeOrigin } = window;
  const phraseStartSec = params.startSec;
  const phraseLenSec = phraseLengthSec(params.lengthSixteenths, params.bpm);

  /** 元音源セグメントを window で切ってスケジュールする */
  const scheduleSegment = (
    compStartSec: number,
    srcOffsetSec: number,
    durSec: number,
  ) => {
    const t0 = Math.max(compStartSec, windowStartSec);
    const t1 = Math.min(compStartSec + durSec, windowEndSec);
    if (t1 - t0 < MIN_SCHEDULE_SEC) return;
    scheduleSlice(
      ctx,
      destination,
      buffer,
      timeOrigin + (t0 - windowStartSec),
      srcOffsetSec + (t0 - compStartSec),
      t1 - t0,
      1,
      sources,
    );
  };

  // フレーズ前後の元音源
  scheduleSegment(0, 0, phraseStartSec);
  const tailOffsetSec = phraseStartSec + phraseLenSec;
  if (tailOffsetSec < buffer.duration) {
    scheduleSegment(
      tailOffsetSec,
      tailOffsetSec,
      buffer.duration - tailOffsetSec,
    );
  }

  // 連打ヒット（window はフレーズ全体を含む前提）
  for (const hit of buildStutterPlan(params)) {
    const compStartSec = phraseStartSec + hit.timeSec;
    scheduleSlice(
      ctx,
      destination,
      buffer,
      timeOrigin + (compStartSec - windowStartSec),
      phraseStartSec,
      hit.intervalSec,
      hit.playbackRate,
      sources,
    );
  }

  return sources;
};

/**
 * 差し替え合成の完成形を元音源のサンプルレート・チャンネル数（最大 2ch）で
 * オフラインレンダリングし、16bit PCM WAV バイト列を返す。
 */
export const renderStutterToWav = async (
  buffer: AudioBuffer,
  params: RendaParams,
): Promise<ArrayBuffer> => {
  const totalSec = compositeTotalSec(buffer, params);
  const sampleRate = buffer.sampleRate;
  const channels = Math.min(2, buffer.numberOfChannels);
  const ctx = new OfflineAudioContext(
    channels,
    Math.ceil(totalSec * sampleRate),
    sampleRate,
  );
  scheduleComposite(ctx, ctx.destination, buffer, params, {
    windowStartSec: 0,
    windowEndSec: totalSec,
    timeOrigin: 0,
  });
  const rendered = await ctx.startRendering();
  return encodeWav(rendered);
};
